'use client';

import { CheckboxGroup, freddyTheme } from '@coolblue-development/freddy';
import deselectFilterTrigger from '@coolblue-development/ga4-events/triggers/FilterAndSort/deselectFilterTrigger';
import selectFilterTrigger from '@coolblue-development/ga4-events/triggers/FilterAndSort/selectFilterTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { memo, useContext, useMemo } from 'react';

import { FilterPageContext } from '../../../context';
import { useFacetValue } from '../../../hooks/useFacetValue';
import type { ValueFacet } from '../../../types';
import CollapsibleContainer from '../CollapsibleContainer';
import { buildFacetValues } from './utils';

export default memo(function ValueFacet({ facet }: { facet: ValueFacet }) {
  const { setFilter, trackingSubFeature } = useContext(FilterPageContext);
  const { trackEvent } = useAnalytics();
  const { values, name, recommendedFacetValue, trackingValue: filter_name } = facet;
  const initialSelectedValues = useMemo(
    () => values.filter(value => value.isSelected).map(value => value.value),
    [values]
  );

  const [currentFacetValue, setCurrentFacetValue] = useFacetValue(initialSelectedValues, ['']);

  const facetValueElements = useMemo(
    () => buildFacetValues(values, recommendedFacetValue),
    [values, recommendedFacetValue]
  );

  if (values.length === 0) {
    return <></>;
  }

  const getChangedValue = (prev: string[], next: string[]): string | undefined => {
    return [...prev, ...next].find(val => !(prev.includes(val) && next.includes(val)));
  };

  const isRecommended = (nextValues: string[]) => {
    const changedValue = getChangedValue(currentFacetValue, nextValues);
    return changedValue === recommendedFacetValue;
  };

  const handleTracking = (nextValues: string[]) => {
    const changedValue = getChangedValue(currentFacetValue, nextValues);
    const matchingValue = values.find(value => value.value === changedValue);
    const filter_value =
      facet.type === 'numeric' ? matchingValue?.displayName : matchingValue?.trackingValue;

    if (!filter_name || !filter_value) return;

    const eventPayload = {
      sub_feature: trackingSubFeature,
      filter_name,
      filter_value,
      number_of_items: 1,
      is_recommended: isRecommended(nextValues)
    } as const;

    const event =
      nextValues.length < currentFacetValue.length
        ? deselectFilterTrigger(eventPayload)
        : selectFilterTrigger(eventPayload);

    trackEvent(event);
  };

  const handleChange = (nextValues: string[]) => {
    handleTracking(nextValues);
    setCurrentFacetValue(nextValues);
    setFilter?.({ name, values: nextValues });
  };

  return (
    <CheckboxGroup
      value={currentFacetValue}
      onChange={handleChange}
      name={name}
      mt={200}
      // CombinedBooleanFacets re-use ValueFacets with a single title for the facet group
      // this fixes the spacing between the fieldsets in that use-case
      sx={{ ':is(fieldset + fieldset)': { marginTop: `${freddyTheme.spacing[100]} !important` } }}
    >
      <CollapsibleContainer displayName={name}>{facetValueElements}</CollapsibleContainer>
    </CheckboxGroup>
  );
});
